import React, { useState, useEffect, useRef } from "react";
import { toast } from "react-toastify";
import JuniorShimmerLoader from "./JuniorShimmerLoader";

const JuniorWorkspace = ({
  mode = "free_play",
  subMode = "basic", // "basic" (ages 5-7, icon-only) | "advanced" (ages 7-10, icon+text)
  initialData = null,
  selectedChild = null,
  readOnly = false,
  onCodeChange = null,
  onGradeSubmit = null,
}) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [activeSubMode, setActiveSubMode] = useState(subMode);
  const iframeRef = useRef(null);

  useEffect(() => {
    // Safety fallback timer for shimmer transition (in case iframe onLoad event fails)
    const timer = setTimeout(() => {
      setIsLoaded(true);
    }, 1200);
    return () => clearTimeout(timer);
  }, []);

  // Send initialData to iframe when available
  const sendInitialDataToIframe = () => {
    if (!initialData || !iframeRef.current || !iframeRef.current.contentWindow) return;
    if (initialData instanceof ArrayBuffer) {
      iframeRef.current.contentWindow.postMessage(
        { type: "LOAD_PROJECT_BUFFER", payload: { buffer: initialData } },
        "*"
      );
    } else if (typeof initialData === "string" && (initialData.startsWith("data:") || initialData.startsWith("UEsDB"))) {
      iframeRef.current.contentWindow.postMessage(
        { type: "LOAD_PROJECT_JSON", payload: initialData },
        "*"
      );
    } else {
      iframeRef.current.contentWindow.postMessage(
        { type: "LOAD_PROJECT", payload: initialData },
        "*"
      );
    }
  };

  useEffect(() => {
    if (isLoaded && initialData) {
      sendInitialDataToIframe();
    }
  }, [initialData, isLoaded]);

  // Listen to messages from the Junior Studio iframe
  useEffect(() => {
    const handleMessage = (event) => {
      if (!event.data) return;
      const { type, payload } = event.data;

      if (type === "JUNIOR_SUBMODE_CHANGED" && payload?.mode) {
        setActiveSubMode(payload.mode);
      }

      if (type === "TRIGGER_CLOUD_SAVE") {
        // Request fresh project JSON and SB3 from iframe for saving
        if (iframeRef.current && iframeRef.current.contentWindow) {
          iframeRef.current.contentWindow.postMessage({ type: "GET_PROJECT_JSON" }, "*");
        }
        setTimeout(() => {
          window.dispatchEvent(new CustomEvent("workspace-save-shortcut"));
        }, 80);
      }

      if (type === "SEND_PROJECT_JSON" && onCodeChange) {
        const targetEnv = subMode === "advanced" ? "junior_blocks_adv" : "junior_blocks_basic";
        onCodeChange(payload, targetEnv);
      }

      if (type === "ASSET_SIZE_LIMIT_EXCEEDED") {
        const { assetType, assetName, incomingSizeBytes } = payload || {};
        const label = assetName ? `"${assetName}"` : (assetType ? assetType.toLowerCase() : "asset");
        const formattedIncoming = incomingSizeBytes ? ` (${(incomingSizeBytes / 1024).toFixed(1)} KB)` : "";
        toast.warning(
          `Project limit (500 KB) reached! Adding ${label}${formattedIncoming} would exceed the allowed project size. Please choose a smaller file or delete unused sprites/costumes.`,
          { autoClose: 6000 }
        );
      }
    };

    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, [onCodeChange, subMode]);

  // Sync subMode when prop changes
  useEffect(() => {
    if (iframeRef.current && iframeRef.current.contentWindow) {
      iframeRef.current.contentWindow.postMessage(
        { type: "SET_JUNIOR_SUBMODE", payload: { mode: subMode } },
        "*"
      );
    }
  }, [subMode]);

  return (
    <div className="w-full h-full bg-[#FAF6EE] overflow-hidden relative flex flex-col select-none min-h-[640px]">
      <JuniorShimmerLoader
        subMode={activeSubMode || subMode}
        isLoaded={isLoaded}
      />

      <iframe
        ref={iframeRef}
        title="Junior Blocks"
        src={`/scratch/junior.html?mode=${subMode || "basic"}`}
        loading="eager"
        className="w-full h-full border-0 block flex-1 min-h-[640px]"
        onLoad={() => {
          setIsLoaded(true);
          if (iframeRef.current && iframeRef.current.contentWindow) {
            iframeRef.current.contentWindow.postMessage(
              { type: "SET_JUNIOR_SUBMODE", payload: { mode: subMode } },
              "*"
            );
            if (initialData) {
              sendInitialDataToIframe();
            } else {
              // Immediately populate activeWorkspaceCode so Save to Cloud is available
              iframeRef.current.contentWindow.postMessage({ type: "GET_PROJECT_JSON" }, "*");
            }
          }
        }}
        className="w-full h-full border-0 block flex-1"
        allow="fullscreen; autoplay"
      />
    </div>
  );
};

export default JuniorWorkspace;
